import { IntegrationEmployeeRecordMappingSettings } from "@performyard/models";

import request from "../base-request";

export type IntegrationName = "bamboohr" | "netchex" | "gusto" | "ukg-pro" | "adp" | "rippling";

/**
 * Maps integration names to UI friendly labels.
 */
const integrationLabelMap: Record<IntegrationName, string> = {
	adp: "ADP",
	bamboohr: "BambooHR",
	gusto: "Gusto",
	netchex: "Netchex",
	rippling: "Rippling",
	"ukg-pro": "UKG Pro",
};

// FETCH COMPANY INTEGRATION INFO

type CompanyIntegrationInfoResponse = {
	connected: boolean;
	lastUpdate: string | null;
	name: IntegrationName;
	needsConsent: boolean;
	organization: string;
	token: string;
	walkthroughStatus: "not-started" | "finished";
	connectUrl: string;
	adpToken: string;
	externalCompanyID: string;
	isCredentialResetting: boolean;
	isPermissionScopeUpdateRequired: boolean;
};

export type FetchCompanyIntegrationInfoResponse = typeof fetchCompanyIntegrationInfo extends () => Promise<infer U>
	? U
	: never;

export const fetchCompanyIntegrationInfo = async () => {
	const [data] = await request<CompanyIntegrationInfoResponse>("/api/integrations/enabled/");

	const label = integrationLabelMap[data.name];

	return {
		...data,
		lastUpdate: data.lastUpdate ? new Date(data.lastUpdate) : null,
		label,
	};
};

// FETCH INTEGRATION SETTINGS

type IntegrationSettingsResponse = {
	deactivate_users_when_not_present: boolean;
	employee_record_mapping_settings?: IntegrationEmployeeRecordMappingSettings;
};

export const fetchIntegrationSettings = async (integration: IntegrationName | null) => {
	const [data] = await request<IntegrationSettingsResponse>(`/api/integrations/${integration}/settings/`);
	return data;
};

// UPDATE INTEGRATION SETTINGS

export type UpdateIntegrationSettingsRequest = {
	integration: IntegrationName;
	data: {
		deactivate_users_when_not_present?: boolean;
		employee_record_mapping_settings?: IntegrationEmployeeRecordMappingSettings;
	};
};

type UpdateIntegrationSettingsResponse = {
	company_name: string;
	type: string;
	deactivate_users_when_not_present: boolean;
};

export const updateIntegrationSettings = async ({ integration, data }: UpdateIntegrationSettingsRequest) => {
	const [response] = await request<UpdateIntegrationSettingsResponse>(`/api/integrations/${integration}/settings/`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
	return response;
};

// FETCH INTEGRATION CREDENTIALS

type UKGIntegrationCredentialsResponse = {
	credentials_status: "active";
	ukg_pro_api_key: string;
	ukg_pro_base_url: string;
	ukg_pro_username: string;
	walkthrough_finished: boolean;
};

export const fetchUKGIntegrationCredentials = async () => {
	const [data] = await request<UKGIntegrationCredentialsResponse>(`/api/integrations/ukg-pro/credentials/`);
	return data;
};

// SEND INTEGRATION CREDENTIALS

export type SendUKGCredentialsRequest = {
	ukg_pro_api_key: string;
	ukg_pro_base_url: string;
	ukg_pro_username: string;
	ukg_pro_password: string;
};

export const sendUKGIntegrationCredentials = async (sendUKGCredentialsRequest: SendUKGCredentialsRequest) => {
	const [response] = await request<{ success: boolean; errors?: string[] }>(
		`/api/integrations/ukg-pro/credentials/`,
		{
			method: "POST",
			body: JSON.stringify(sendUKGCredentialsRequest),
		}
	);

	return response;
};
